import { useState } from 'react'
import { Alert, Box, Snackbar, Stack, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CloseIcon from '@mui/icons-material/Close'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useCustomMutation, useInvalidate } from '@refinedev/core'
import { Button } from '@ds/components'
import { CARD_COLORS } from '@ds/theme/color-mode'
import { formatApiDate, formatApiDay, PublisherApproveRequestStatus } from '@modules/api-core'
import {
  APPROVAL_ANSWER_STRINGS,
  ApprovalAnswerFields,
  isApprovalAnswered,
  NOT_ANSWERED,
  type TApprovalAnswer,
} from '@modules/approval-answer'
import type { TPublisherSong } from '@modules/types'
import { COMMON_APPROVAL_STRINGS } from './strings'
import type { TPublisherApprovalRequestShowProps } from '../types'

const findDecision = (song: TPublisherSong) => {
  if (!song.approvalDecision) return null
  if (song.approvalDecision.isApproved === 0) return 'rejected'
  return song.counterOfferFees?.[0] ? 'counteroffered' : 'approved'
}

const DECISION_SX = {
  approved: { borderColor: CARD_COLORS.paid, color: CARD_COLORS.approvedText },
  rejected: { borderColor: CARD_COLORS.red, color: CARD_COLORS.rejectFill },
  counteroffered: { borderColor: 'text.primary', color: 'text.primary' },
} as const

const DECISION_ICONS = {
  approved: <CheckIcon fontSize="small" />,
  rejected: <CloseIcon fontSize="small" />,
  counteroffered: <EditOutlinedIcon fontSize="small" />,
} as const

const formatPrice = (price: string | number, currency?: string | null) =>
  currency
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(Number(price))
    : String(price)

const findReasonLabel = (reason: string) =>
  Object.entries(APPROVAL_ANSWER_STRINGS.rejectReasons).find(([key]) => key === reason)?.[1] ??
  reason

const labelSx = { m: 0, mb: 0.5, fontSize: 13, color: 'text.secondary' } as const
const valueSx = { m: 0, fontSize: 22, fontWeight: 700 } as const
const currencySx = { fontSize: 14, fontWeight: 500, color: 'text.secondary' } as const

export const PublisherCommonApprovalRequest = ({
  requestId,
  selectedRequest,
  initialLoading,
  apiUrl,
  onDrawerClose,
  onReviewSuccess,
  isRequestSubmitted,
}: TPublisherApprovalRequestShowProps) => {
  const [answers, setAnswers] = useState<Record<number, TApprovalAnswer>>({})
  const [error, setError] = useState(false)
  const { mutate: sendReview, isPending } = useCustomMutation()
  const invalidate = useInvalidate()

  const songs = selectedRequest.songs ?? []
  const withdrawn = Boolean(selectedRequest.withdrawnAt)
  const answered =
    selectedRequest.approveStatus === PublisherApproveRequestStatus.APPROVED ||
    selectedRequest.approveStatus === PublisherApproveRequestStatus.PARTIALLY_APPROVED ||
    selectedRequest.approveStatus === PublisherApproveRequestStatus.REJECTED ||
    selectedRequest.approveStatus === PublisherApproveRequestStatus.PENDING_REVIEW

  const answerSong = (songId: number, change: Partial<TApprovalAnswer>) =>
    setAnswers(previous => ({
      ...previous,
      [songId]: { ...NOT_ANSWERED, ...previous[songId], ...change },
    }))

  const confirm = () => {
    setError(false)
    sendReview(
      {
        url: `${apiUrl}/publisher-request/${requestId}/review`,
        method: 'post',
        values: {
          answerReceivedOn: formatApiDay(new Date().toISOString()),
          songs: songs.map(song => ({
            songId: song.id,
            isApproved: answers[song.id].choice !== 'reject',
            rejectReason: answers[song.id].choice === 'reject' ? answers[song.id].reason : null,
            rejectDescription:
              answers[song.id].choice === 'reject' && answers[song.id].reason === 'other'
                ? answers[song.id].description.trim()
                : null,
            comment: answers[song.id].comment.trim() || null,
            isCounterOffered: answers[song.id].choice === 'counteroffer',
            counterOfferFee:
              answers[song.id].choice === 'counteroffer' ? answers[song.id].fee : null,
          })),
        },
      },
      {
        onSuccess: () => {
          onDrawerClose()
          invalidate({
            resource: 'publisher-request',
            id: requestId,
            invalidates: ['list'],
          })
          onReviewSuccess()
        },
        onError: () => {
          setError(true)
        },
      },
    )
  }

  const disabled = isPending || initialLoading || isRequestSubmitted || answered
  const answeredAt = songs
    .map(song => song.approvalDecision?.createdAt ?? '')
    .sort()
    .pop()
  const answeredCount = songs.filter(song =>
    isApprovalAnswered(answers[song.id] ?? NOT_ANSWERED, song.offeredPrice),
  ).length

  return (
    <Box display="flex" flexDirection="column" flex={1} sx={{ bgcolor: 'background.paper' }}>
      <Stack spacing={2.75} sx={{ flex: 1, overflowY: 'auto', px: 4, pt: 2.75, pb: 4 }}>
        {!withdrawn &&
          (answered ? (
          <Stack
            role="status"
            direction="row"
            alignItems="center"
            gap={1.5}
            sx={{
              bgcolor: CARD_COLORS.answeredBg,
              color: CARD_COLORS.answeredText,
              px: 2.5,
              py: 2,
              fontSize: 15,
            }}
          >
            <CheckCircleOutlineIcon fontSize="small" />
            {COMMON_APPROVAL_STRINGS.answered(
              answeredAt && formatApiDate(answeredAt),
            )}
          </Stack>
        ) : (
          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems="center"
            gap={1.5}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={() => songs.forEach(song => answerSong(song.id, { choice: 'approve' }))}
              disabled={disabled}
              sx={{
                minHeight: 44,
                px: 2.5,
                fontSize: 15,
                fontWeight: 600,
                borderColor: 'text.primary',
              }}
            >
              {COMMON_APPROVAL_STRINGS.approveAll}
            </Button>
          </Stack>
          ))}

        {songs.map(song => {
          const decision = findDecision(song)
          const offer = song.counterOfferFees?.[0]
          return (
            <Stack
              component="article"
              key={song.id}
              direction={withdrawn ? 'row' : 'column'}
              flexWrap={withdrawn ? 'wrap' : 'nowrap'}
              justifyContent={withdrawn ? 'space-between' : 'flex-start'}
              gap={answered || withdrawn ? 2 : 2.25}
              sx={{
                bgcolor: 'background.default',
                borderRadius: 1,
                px: 3.5,
                py: 3,
                color: withdrawn ? CARD_COLORS.withdrawnValue : 'text.primary',
              }}
            >
              <Stack
                direction="row"
                flexWrap="wrap"
                justifyContent="space-between"
                alignItems="flex-start"
                gap={2}
              >
                <Box>
                  <Typography sx={labelSx}>{song.clientRequestSong?.writers}</Typography>
                  <Typography component="h3" sx={{ m: 0, fontSize: 24, fontWeight: 700 }}>
                    {song.clientRequestSong?.title}
                  </Typography>
                </Box>
                {answered && decision && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{
                      minHeight: 44,
                      px: 2.5,
                      borderRadius: 999,
                      border: 1,
                      fontSize: 15,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      ...DECISION_SX[decision],
                    }}
                  >
                    {DECISION_ICONS[decision]}
                    {COMMON_APPROVAL_STRINGS[decision]}
                  </Stack>
                )}
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={answered ? 5 : 4}>
                {song.offeredPrice && (
                  <Box>
                    <Typography sx={labelSx}>{COMMON_APPROVAL_STRINGS.fee}</Typography>
                    <Typography
                      sx={{
                        ...valueSx,
                        ...(answered &&
                          offer && {
                            color: 'text.secondary',
                            textDecoration: 'line-through',
                          }),
                      }}
                    >
                      {formatPrice(song.offeredPrice, song.offeredCurrency)}{' '}
                      {!(answered && offer) && (
                        <Box component="span" sx={currencySx}>
                          {song.offeredCurrency}
                        </Box>
                      )}
                    </Typography>
                  </Box>
                )}
                {answered && offer && (
                  <Box>
                    <Typography sx={labelSx}>{COMMON_APPROVAL_STRINGS.yourPrice}</Typography>
                    <Typography sx={valueSx}>
                      {formatPrice(offer.proposedFee, song.offeredCurrency)}{' '}
                      <Box component="span" sx={currencySx}>
                        {song.offeredCurrency}
                      </Box>
                    </Typography>
                  </Box>
                )}
                {song.offeredCopies && (
                  <Box>
                    <Typography sx={labelSx}>{COMMON_APPROVAL_STRINGS.copies}</Typography>
                    <Typography sx={valueSx}>
                      {song.offeredCopies.toLocaleString('en-US')}
                    </Typography>
                  </Box>
                )}
              </Stack>
              {answered && (song.approvalDecision?.rejectReason || song.approvalDecision?.comment) && (
                <Stack gap={1.25} sx={{ borderTop: 1, borderColor: 'divider', pt: 1.75 }}>
                  {song.approvalDecision.rejectReason && (
                    <Box>
                      <Typography sx={labelSx}>{COMMON_APPROVAL_STRINGS.reason}</Typography>
                      <Typography sx={{ m: 0, fontSize: 15 }}>
                        {[
                          findReasonLabel(song.approvalDecision.rejectReason),
                          song.approvalDecision.rejectDescription,
                        ]
                          .filter(Boolean)
                          .join(' — ')}
                      </Typography>
                    </Box>
                  )}
                  {song.approvalDecision.comment && (
                    <Box>
                      <Typography sx={labelSx}>{COMMON_APPROVAL_STRINGS.comment}</Typography>
                      <Typography sx={{ m: 0, fontSize: 15 }}>
                        {song.approvalDecision.comment}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
              {!answered && !withdrawn && (
                <ApprovalAnswerFields
                  answer={answers[song.id] ?? NOT_ANSWERED}
                  offeredPrice={song.offeredPrice}
                  offeredCurrency={song.offeredCurrency}
                  disabled={disabled}
                  onChange={change => answerSong(song.id, change)}
                />
              )}
            </Stack>
          )
        })}

        {!answered && !withdrawn && (
          <Stack alignItems="center" gap={1.5} sx={{ mt: 'auto', pt: 1.5 }}>
            <Typography sx={{ m: 0, fontSize: 15, color: 'text.secondary' }}>
              {COMMON_APPROVAL_STRINGS.answeredCount(answeredCount, songs.length)}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={confirm}
              disabled={disabled || songs.length === 0 || answeredCount < songs.length}
              loading={isPending}
              sx={{ minHeight: 52, fontSize: 16, fontWeight: 600 }}
            >
              {COMMON_APPROVAL_STRINGS.confirm}
            </Button>
          </Stack>
        )}
      </Stack>
      <Snackbar open={error} onClose={() => setError(false)}>
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={confirm}>
              {COMMON_APPROVAL_STRINGS.retry}
            </Button>
          }
        >
          {COMMON_APPROVAL_STRINGS.error}
        </Alert>
      </Snackbar>
    </Box>
  )
}
